/**
 * Layout maths for SelectionPane's below-bar
 */

const SEPARATOR_WIDTH = 10;
const LIST_PADDING = 8;
const PANE_RIGHT_PADDING = 4;
export const PROVIDER_WIDTH = 170;
const NUM_PROVIDERS = 2;
export const HELP_WIDTH = PROVIDER_WIDTH * NUM_PROVIDERS;
export const MIN_GROUP_PANE_WIDTH = 300;

const NUM_PADDED_SECTIONS = 4;
const NUM_SEPARATORS = 2;

const NUM_LISTS = 2;
const MIN_LIST_WIDTH = 150;
const DEFAULT_WEIGHT = 0.3;
const NARROW_WEIGHT = 0.5;

const WEIGHT_PRECISION = 10000;

const SNAP_SIZE = 0.5;
const SNAP_THRESHOLD = 0.025;

type TExtraPanes = {
    hasProvider: boolean;
    hasTemplateHelp: boolean;
    hasGroupPane: boolean;
};

type TListWeights = {
    weightObjectList: number;
    weightTypeList: number;
};

type TListWidths = {
    objectListWidth: number;
    selectedListWidth: number;
};

export const getAvailableWidth = (paneWidth: number): number =>
    paneWidth -
    LIST_PADDING * NUM_PADDED_SECTIONS -
    SEPARATOR_WIDTH * NUM_SEPARATORS -
    PANE_RIGHT_PADDING;

const getRemainingWidth = (
    availableWidth: number,
    { objectListWidth, selectedListWidth }: TListWidths,
    alreadyClaimed: number
): number => availableWidth - objectListWidth - selectedListWidth - alreadyClaimed;

const shrinkLists = ({ objectListWidth, selectedListWidth }: TListWidths, needed: number) => ({
    objectListWidth: objectListWidth - needed / NUM_LISTS,
    selectedListWidth: selectedListWidth - needed / NUM_LISTS,
});

export const getListWidths = (
    availableWidth: number,
    { weightObjectList, weightTypeList }: TListWeights,
    isNarrow: boolean,
    { hasProvider, hasTemplateHelp, hasGroupPane }: TExtraPanes
): TListWidths => {
    let widths: TListWidths = isNarrow
        ? {
              objectListWidth: Math.floor(NARROW_WEIGHT * availableWidth),
              selectedListWidth: Math.floor(NARROW_WEIGHT * availableWidth),
          }
        : {
              objectListWidth: Math.floor((weightObjectList || DEFAULT_WEIGHT) * availableWidth),
              selectedListWidth: Math.floor((weightTypeList || DEFAULT_WEIGHT) * availableWidth),
          };

    if (hasProvider) {
        const remaining = getRemainingWidth(availableWidth, widths, 0);
        if (remaining < PROVIDER_WIDTH) {
            widths = shrinkLists(widths, PROVIDER_WIDTH - remaining);
        }
    }

    if (hasTemplateHelp) {
        const remaining = getRemainingWidth(
            availableWidth,
            widths,
            hasProvider ? PROVIDER_WIDTH : 0
        );
        if (remaining < HELP_WIDTH) {
            widths = shrinkLists(widths, HELP_WIDTH - remaining);
        }
    }

    if (hasGroupPane) {
        const remaining = getRemainingWidth(
            availableWidth,
            widths,
            (hasProvider ? PROVIDER_WIDTH : 0) + (hasTemplateHelp ? HELP_WIDTH : 0)
        );
        if (remaining < MIN_GROUP_PANE_WIDTH) {
            widths = shrinkLists(widths, MIN_GROUP_PANE_WIDTH);
        }
    }

    return widths;
};

/**
 * Returns null when the drag would make either list too narrow to use, in which
 * case the caller should leave the current weights alone.
 */
export const getResizeWeights = (
    availableWidth: number,
    pointerOffsetX: number,
    resizedComponentIndex: number,
    { weightObjectList, weightTypeList }: TListWeights
): TListWeights | null => {
    let objectWeight = weightObjectList;
    let typeWeight = weightTypeList;

    const newWeight =
        Math.floor((WEIGHT_PRECISION * pointerOffsetX) / availableWidth) / WEIGHT_PRECISION;

    if (resizedComponentIndex === 0) {
        objectWeight = newWeight;
    } else {
        typeWeight = newWeight - objectWeight;
    }

    if (objectWeight + typeWeight > 1) {
        typeWeight = 1 - objectWeight;
    }

    if (objectWeight && !typeWeight) {
        typeWeight = 1 - objectWeight;
    }

    if (
        Math.abs(objectWeight - SNAP_SIZE) < SNAP_THRESHOLD &&
        Math.abs(typeWeight - SNAP_SIZE) < SNAP_THRESHOLD
    ) {
        objectWeight = SNAP_SIZE;
        typeWeight = SNAP_SIZE;
    }

    if (
        objectWeight * availableWidth < MIN_LIST_WIDTH ||
        typeWeight * availableWidth < MIN_LIST_WIDTH
    ) {
        return null;
    }

    return { weightObjectList: objectWeight, weightTypeList: typeWeight };
};
